import { useState } from 'react';

import {
  Alert,
  Button,
  Calendario,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
} from '@/components/ui';
import { useUsers } from '@/lib/admin-store';
import { useActivityTypes } from '@/lib/activity-types-store';
import { api, ApiError } from '@/lib/api';
import {
  combineDateAndTime,
  isoToDateOnly,
  TaskConflictError,
  useTaskActions,
  type ConflictPayload,
  type CreateTaskInput,
  type Task,
  type TaskTimeMode,
} from '@/lib/tasks-store';

import { ConflictModal } from './ConflictModal';
// UX-3 / FEAT-V2-018: preview de agenda inline + check antes do submit.
import { AgendaPreview } from './AgendaPreview';

/**
 * `<CreateTaskDialog>` — Modal de criar tarefa no card (U.1 / BUG-V2-016).
 *
 * 2 modos de horário (start_only, start_end) — o modo só-data (no_time)
 * morreu no #75: agendamento é SEMPRE data + hora, escolhidas no
 * <Calendario> canônico. Quando o backend detecta conflito Calendar (409),
 * o componente abre `<ConflictModal>` pra exibir os eventos conflitantes +
 * permitir "agendar mesmo assim".
 *
 * `tasksFeatureSyncEnabled` (prop opcional) controla se o toggle de modo
 * aparece — quando OFF, força `start_only` (a hora fica; só some o Fim).
 */

interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  cardId: string;
  workflowSlug: string;
  onCreated?: (task: Task) => void;
  /** Quando false, esconde toggle (modo no_time forçado). Default true. */
  tasksFeatureSyncEnabled?: boolean;
}

// #75 (FILA-05AGO): 'no_time' saiu do menu — agendamento é SEMPRE data + hora
// (regra permanente do CLAUDE.md). O tipo do backend ainda conhece no_time
// (tarefas antigas existem), mas o diálogo não cria mais nenhuma.
type ModoComHora = Exclude<TaskTimeMode, 'no_time'>;
const MODOS_COM_HORA: ModoComHora[] = ['start_only', 'start_end'];
const TIME_MODE_LABELS: Record<ModoComHora, string> = {
  start_only: 'Horário inicial',
  start_end: 'Horário inicial e final',
};

export function CreateTaskDialog({
  open,
  onClose,
  cardId,
  workflowSlug,
  onCreated,
  tasksFeatureSyncEnabled = true,
}: CreateTaskDialogProps) {
  const users = useUsers();
  const actions = useTaskActions(cardId, workflowSlug);
  // BUG-V2-021 / UX-2: campo Título substituído por Tipo (activity_type categoria='task').
  // Backend auto-popula titulo com activity_type.label se vazio.
  const { data: activityTypes = [] } = useActivityTypes({
    categoria: 'task',
    workflow: workflowSlug,
  });

  const [activityTypeId, setActivityTypeId] = useState('');
  const [descricao, setDescricao] = useState('');
  const [responsavelId, setResponsavelId] = useState('');
  const [timeMode, setTimeMode] = useState<ModoComHora>('start_only');
  // Data + hora vêm do <Calendario> num gesto só; o Fim continua um time input.
  const [dateStr, setDateStr] = useState(isoToDateOnly(new Date().toISOString()));
  const [startTimeStr, setStartTimeStr] = useState('09:00');
  const [endTimeStr, setEndTimeStr] = useState('10:00');
  // UI-3 / TD-V2-PERM-07: lista de userIds convidados pra tarefa (multi-select).
  // Submetida após criar task via POST /api/v1/task-invitations/tasks/:id/invitations.
  // O responsável NÃO entra aqui (gerenciado separadamente).
  const [convidados, setConvidados] = useState<string[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [inviteWarning, setInviteWarning] = useState<string | null>(null);
  const [conflictPayload, setConflictPayload] = useState<ConflictPayload | null>(null);
  const [pendingInput, setPendingInput] = useState<CreateTaskInput | null>(null);

  // Flag OFF só esconde o toggle (sem sync não há start_end) — a hora fica:
  // agendamento sem hora não existe mais (#75).
  const effectiveTimeMode: ModoComHora = tasksFeatureSyncEnabled ? timeMode : 'start_only';

  function reset() {
    setActivityTypeId('');
    setDescricao('');
    setResponsavelId('');
    setTimeMode('start_only');
    setStartTimeStr('09:00');
    setEndTimeStr('10:00');
    setConvidados([]);
    setError(null);
    setInviteWarning(null);
    setConflictPayload(null);
    setPendingInput(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function buildPayload(): CreateTaskInput {
    // BUG-V2-021: titulo é auto-populado do activity_type.label.
    // Backend valida (titulo obrigatório no schema Zod), então sempre preenche.
    const selectedType = activityTypes.find((t) => t.id === activityTypeId);
    const tituloAuto = selectedType?.label ?? '';
    const base: CreateTaskInput = {
      workflowSlug,
      titulo: tituloAuto,
      descricao: descricao.trim() || null,
      responsavelId: responsavelId || null,
      activityTypeId: activityTypeId || null,
      timeMode: effectiveTimeMode,
    };
    if (effectiveTimeMode === 'start_only') {
      base.startAtIso = combineDateAndTime(dateStr, startTimeStr);
      base.endAtIso = null;
    } else {
      base.startAtIso = combineDateAndTime(dateStr, startTimeStr);
      base.endAtIso = combineDateAndTime(dateStr, endTimeStr);
    }
    return base;
  }

  function validateClient(input: CreateTaskInput): string | null {
    if (!input.activityTypeId) return 'Tipo obrigatório';
    if (!input.titulo) return 'Tipo selecionado não tem label (admin precisa corrigir)';
    if (!dateStr || !startTimeStr) return 'Escolha data e hora pra agendar.';
    if (input.timeMode === 'start_end' && input.startAtIso && input.endAtIso) {
      if (Date.parse(input.endAtIso) <= Date.parse(input.startAtIso)) {
        return 'Fim deve ser depois do início';
      }
    }
    return null;
  }

  async function submitPayload(payload: CreateTaskInput): Promise<void> {
    setError(null);
    setInviteWarning(null);
    try {
      const task = await actions.createTask(payload);
      // UI-3 / TD-V2-PERM-07: dispatch invitations BEST-EFFORT — falhar
      // aqui NÃO desfaz a task. Se feature flag desativada (503), só avisa.
      if (convidados.length > 0) {
        try {
          await api.post(
            `/api/v1/task-invitations/tasks/${encodeURIComponent(task.id)}/invitations`,
            { userIds: convidados },
          );
        } catch (inviteErr) {
          if (inviteErr instanceof ApiError && inviteErr.status === 503) {
            setInviteWarning(
              'Tarefa criada, mas convites estão desativados. Pergunte ao admin.',
            );
          } else {
            const msg =
              inviteErr instanceof Error ? inviteErr.message : 'erro desconhecido';
            setInviteWarning(`Tarefa criada, mas convites falharam: ${msg}`);
          }
          // Não fecha o modal — user vê o aviso.
          onCreated?.(task);
          return;
        }
      }
      onCreated?.(task);
      handleClose();
    } catch (err) {
      if (err instanceof TaskConflictError) {
        // Salva payload pra re-submit com forceConflict
        setConflictPayload(err.payload);
        setPendingInput(payload);
        return;
      }
      setError(err instanceof Error ? err.message : 'Erro ao criar tarefa');
    }
  }

  async function handleSubmit() {
    const payload = buildPayload();
    const validationErr = validateClient(payload);
    if (validationErr) {
      setError(validationErr);
      return;
    }
    await submitPayload(payload);
  }

  async function handleForceCreate() {
    if (!pendingInput) return;
    setConflictPayload(null);
    await submitPayload({ ...pendingInput, forceConflict: true });
  }

  function handleCancelConflict() {
    setConflictPayload(null);
    setPendingInput(null);
    // Mantém modal de criar aberto pro user ajustar
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && handleClose()} size="md">
        <DialogHeader>
          <DialogTitle>Nova tarefa</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <div className="space-y-3" data-testid="create-task-dialog">
            {error && <Alert variant="danger" rail>{error}</Alert>}
            {inviteWarning && (
              <Alert
                variant="warning"
                rail
                data-testid="task-invite-warning"
              >
                {inviteWarning}
              </Alert>
            )}

            <div>
              <Label htmlFor="task-tipo" required>Tipo</Label>
              <Select
                id="task-tipo"
                value={activityTypeId}
                onChange={(e) => setActivityTypeId(e.target.value)}
                data-testid="task-tipo"
              >
                <option value="">Selecione um tipo…</option>
                {activityTypes
                  .filter((t) => t.ativo)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.icone ? `${t.icone} ` : ''}{t.label}
                    </option>
                  ))}
              </Select>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                O título da tarefa será definido pelo tipo selecionado.
              </p>
            </div>

            <div>
              <Label htmlFor="task-descricao">Descrição</Label>
              <textarea
                id="task-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={2}
                className="w-full border border-line dark:border-line-dark rounded-lg px-3 py-2 text-sm bg-paper-surface dark:bg-paper-dark-surface"
                placeholder="Detalhes (opcional)"
              />
            </div>

            <div>
              <Label htmlFor="task-responsavel">Responsável</Label>
              <Select
                id="task-responsavel"
                value={responsavelId}
                onChange={(e) => setResponsavelId(e.target.value)}
                data-testid="task-responsavel"
              >
                <option value="">— Sem responsável —</option>
                {users
                  .filter((u) => u.ativo)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
              </Select>
            </div>

            {/* UI-3 / TD-V2-PERM-07: convidados (multi-select). */}
            <div>
              <Label htmlFor="task-convidados">Convidados</Label>
              <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-1">
                Convidados recebem notificação pra aceitar/recusar. Se aceitarem,
                a tarefa aparece no Calendar deles.
              </p>
              <div
                data-testid="task-convidados-list"
                className="flex flex-wrap gap-1 mb-1.5 min-h-[24px]"
              >
                {convidados.map((id) => {
                  const u = users.find((x) => x.id === id);
                  return (
                    <span
                      key={id}
                      data-testid={`task-convidado-chip-${id}`}
                      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-pill bg-paper-warm dark:bg-paper-dark-warm"
                    >
                      {u?.nome ?? id}
                      <button
                        type="button"
                        onClick={() =>
                          setConvidados((prev) => prev.filter((x) => x !== id))
                        }
                        className="text-ink-3 hover:text-red"
                        aria-label={`Remover ${u?.nome ?? id}`}
                        data-testid={`task-convidado-remove-${id}`}
                      >
                        ✕
                      </button>
                    </span>
                  );
                })}
              </div>
              <Select
                id="task-convidados"
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  setConvidados((prev) => (prev.includes(v) ? prev : [...prev, v]));
                }}
                data-testid="task-convidados-select"
              >
                <option value="">+ Adicionar convidado…</option>
                {users
                  .filter(
                    (u) =>
                      u.ativo &&
                      !convidados.includes(u.id) &&
                      u.id !== responsavelId,
                  )
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
              </Select>
            </div>

            {/* Toggle de modo (só aparece se sync ativado). no_time morreu (#75). */}
            {tasksFeatureSyncEnabled && (
              <Card className="bg-paper-warm dark:bg-paper-dark-warm">
                <div className="p-3 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-ink-2 dark:text-ink-dark-2">
                    Quando
                  </div>
                  {MODOS_COM_HORA.map((mode) => (
                    <label
                      key={mode}
                      className="flex items-center gap-2 text-sm cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="task-time-mode"
                        value={mode}
                        checked={timeMode === mode}
                        onChange={() => setTimeMode(mode)}
                        data-testid={`time-mode-${mode}`}
                      />
                      <span>{TIME_MODE_LABELS[mode]}</span>
                    </label>
                  ))}
                </div>
              </Card>
            )}

            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <Label>Data e hora de início</Label>
                <Calendario
                  valor={dateStr && startTimeStr ? `${dateStr}T${startTimeStr}` : ''}
                  onChange={(v) => {
                    setDateStr(v ? v.slice(0, 10) : '');
                    setStartTimeStr(v ? v.slice(11, 16) : '');
                  }}
                  placeholder="Escolher data e hora"
                  testId="task-quando-calendario"
                />
              </div>

              {effectiveTimeMode === 'start_end' && (
                <div>
                  <Label htmlFor="task-end-time">Fim</Label>
                  <Input
                    id="task-end-time"
                    type="time"
                    value={endTimeStr}
                    onChange={(e) => setEndTimeStr(e.target.value)}
                    data-testid="task-end-time"
                  />
                </div>
              )}
            </div>

            {/* UX-3: AgendaPreview inline com a janela escolhida. */}
            {dateStr && startTimeStr && (
              <AgendaPreview
                startIso={combineDateAndTime(dateStr, startTimeStr)}
                endIso={
                  effectiveTimeMode === 'start_end'
                    ? combineDateAndTime(dateStr, endTimeStr)
                    : null
                }
              />
            )}
          </div>
        </DialogContent>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            loading={actions.pending === 'create'}
            data-testid="create-task-submit"
          >
            Criar tarefa
          </Button>
        </DialogFooter>
      </Dialog>

      {conflictPayload && (
        <ConflictModal
          payload={conflictPayload}
          onCancel={handleCancelConflict}
          onForceCreate={handleForceCreate}
          loading={actions.pending === 'create'}
        />
      )}
    </>
  );
}
